import { type ErrorReportingPolicy, setErrorReportingPolicy, useErrorBoundary } from "@wayflyer/blackbox-fe";
import type { ErrorComponentProps, FlyError } from "@wayflyer/flyui";
import { useCallback, useEffect, useState } from "react";

export type ErrorPageOptions = Pick<
  FlyError,
  "error" | "title" | "message" | "footerText" | "primaryAction" | "secondaryAction"
> & {
  /** Set before forwarding to the boundary; cannot change a report that already happened. */
  reporting?: ErrorReportingPolicy;
};

// Keep the original exception intact for HTTP classification and report-once logging.
const errorPages = new WeakMap<Error, ErrorComponentProps>();

/**
 * Replaces the nearest supported boundary's content with a custom error page.
 * Accepts a FlyError or plain ErrorPageOptions. Logging is owned by the boundary;
 * FlyError's context and suppressLog options are not supported here.
 * Use reporting to classify expected failures before the boundary reports them.
 */
export const useErrorPage = () => {
  const { showBoundary } = useErrorBoundary();

  const showErrorPage = useCallback(
    ({ error, title, message, footerText, primaryAction, secondaryAction, reporting }: ErrorPageOptions) => {
      if (reporting !== undefined) {
        setErrorReportingPolicy(error, reporting);
      }
      errorPages.set(error, { title, message, footerText, primaryAction, secondaryAction });
      showBoundary(error);
    },
    [showBoundary]
  );

  return { showErrorPage };
};

const getPresentation = (error: unknown) => ({
  error,
  props: error instanceof Error ? errorPages.get(error) : undefined,
});

/** Resolves a custom page or the supplied preset, clearing custom copy when the fallback leaves. */
export const useErrorPageProps = (error: unknown, preset: ErrorComponentProps): ErrorComponentProps => {
  // Snapshot per exception so StrictMode's effect cleanup cannot erase the displayed copy.
  const [presentation, setPresentation] = useState(() => getPresentation(error));
  if (!Object.is(presentation.error, error)) {
    setPresentation(getPresentation(error));
  }

  useEffect(
    () => () => {
      if (presentation.error instanceof Error && errorPages.get(presentation.error) === presentation.props) {
        errorPages.delete(presentation.error);
      }
    },
    [presentation]
  );

  if (!presentation.props) {
    return preset;
  }

  const custom = presentation.props;
  return {
    ...custom,
    // Custom actions replace preset actions; supply one safe way out only when neither was provided.
    primaryAction:
      custom.primaryAction ?? (custom.secondaryAction ? undefined : preset.primaryAction ?? preset.secondaryAction),
  };
};
